import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import SEOHead from '@/components/feature/SEOHead';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const MOCK_STATS = {
  totalEarnings: 3842.5,
  pendingPayout: 680.0,
  thisMonth: 1240.0,
  totalReferrals: 127,
  activeReferrals: 89,
  conversionRate: 70.1,
};

const MOCK_REFERRALS = [
  { id: 'R001', name: '张**', plan: '黄金会员', date: '2026-04-10', commission: 30, status: 'paid' },
  { id: 'R002', name: '李**', plan: '工厂会员', date: '2026-04-09', commission: 5, status: 'paid' },
  { id: 'R003', name: '王**', plan: '黄金会员', date: '2026-04-08', commission: 30, status: 'pending' },
  { id: 'R004', name: '陈**', plan: '黄金会员', date: '2026-04-07', commission: 30, status: 'paid' },
  { id: 'R005', name: '刘**', plan: '工厂会员', date: '2026-04-06', commission: 5, status: 'pending' },
  { id: 'R006', name: '赵**', plan: '黄金会员', date: '2026-04-05', commission: 30, status: 'paid' },
  { id: 'R007', name: '孙**', plan: '黄金会员', date: '2026-04-04', commission: 30, status: 'paid' },
  { id: 'R008', name: '周**', plan: '工厂会员', date: '2026-04-03', commission: 5, status: 'pending' },
  { id: 'R009', name: '吴**', plan: '黄金会员', date: '2026-04-02', commission: 30, status: 'paid' },
  { id: 'R010', name: '郑**', plan: '黄金会员', date: '2026-04-01', commission: 30, status: 'paid' },
];

const MOCK_PAYOUTS = [
  { id: 'P2026-04', period: '2026年4月', amount: 1240.0, method: 'PayPal', status: 'processing', date: '2026-04-15' },
  { id: 'P2026-03', period: '2026年3月', amount: 980.5, method: 'PayPal', status: 'paid', date: '2026-03-15' },
  { id: 'P2026-02', period: '2026年2月', amount: 762.0, method: '银行转账', status: 'paid', date: '2026-02-15' },
  { id: 'P2026-01', period: '2026年1月', amount: 860.0, method: 'PayPal', status: 'paid', date: '2026-01-15' },
];

const MONTHLY_DATA = [
  { month: '11月', amount: 420 },
  { month: '12月', amount: 580 },
  { month: '1月', amount: 860 },
  { month: '2月', amount: 762 },
  { month: '3月', amount: 980 },
  { month: '4月', amount: 1240 },
];

const maxAmount = Math.max(...MONTHLY_DATA.map(d => d.amount));

type Tab = 'overview' | 'referrals' | 'payouts' | 'links';

export default function ResellerDashboard() {
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [copied, setCopied] = useState(false);
  const [linkType, setLinkType] = useState<'main' | 'membership' | 'shop'>('main');
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation();

  const referralLinks = {
    main: 'https://fortunematch.com/?ref=RS88821',
    membership: 'https://fortunematch.com/membership?ref=RS88821',
    shop: 'https://fortunematch.com/shop?ref=RS88821',
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: 'overview', label: '数据概览', icon: 'ri-dashboard-line' },
    { id: 'referrals', label: '推广记录', icon: 'ri-user-add-line' },
    { id: 'payouts', label: '收益结算', icon: 'ri-money-cny-circle-line' },
    { id: 'links', label: '推广链接', icon: 'ri-links-line' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-16 md:pb-0" style={{ fontFamily: "'Noto Sans SC', sans-serif" }}>
      <SEOHead
        title="分销商后台 · 富缘佳旺 FortuneMatch"
        description="富缘佳旺分销商专属后台，追踪推广数据、佣金收益与结算记录。"
        keywords="富缘佳旺分销商,佣金追踪,推广后台"
        canonical="/reseller"
      />
      <Navbar />

      {/* Header Banner */}
      <div className="pt-20 bg-gradient-to-r from-emerald-600 to-teal-500">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-1.5 mb-3">
                <i className="ri-share-line text-white text-sm"></i>
                <span className="text-white text-xs font-semibold">分销商专属后台</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-1" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                欢迎回来，分销商 RS88821
              </h1>
              <p className="text-white/70 text-sm">
                <i className="ri-checkbox-circle-line mr-1 text-green-300"></i>
                账号已认证 · 分销商会员 · 永久免费
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <div className="bg-white/15 backdrop-blur-sm rounded-xl px-5 py-3 text-center">
                <div className="text-2xl font-bold text-white">¥{MOCK_STATS.pendingPayout.toFixed(0)}</div>
                <div className="text-white/70 text-xs mt-0.5">待结算佣金</div>
              </div>
              <div className="bg-white/15 backdrop-blur-sm rounded-xl px-5 py-3 text-center">
                <div className="text-2xl font-bold text-white">{MOCK_STATS.totalReferrals}</div>
                <div className="text-white/70 text-xs mt-0.5">累计推广人数</div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-8 overflow-x-auto pb-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-t-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-gray-50 text-emerald-700'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                <i className={tab.icon}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div>
            {/* Stat Cards */}
            <div
              ref={statsRef}
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8 transition-all duration-700"
              style={{ opacity: statsVisible ? 1 : 0, transform: statsVisible ? 'translateY(0)' : 'translateY(20px)' }}
            >
              {[
                { label: '累计总收益', value: `¥${MOCK_STATS.totalEarnings.toLocaleString()}`, icon: 'ri-trophy-line', color: 'text-amber-500', bg: 'bg-amber-50' },
                { label: '本月收益', value: `¥${MOCK_STATS.thisMonth.toLocaleString()}`, icon: 'ri-calendar-line', color: 'text-emerald-500', bg: 'bg-emerald-50' },
                { label: '待结算', value: `¥${MOCK_STATS.pendingPayout.toFixed(0)}`, icon: 'ri-time-line', color: 'text-orange-500', bg: 'bg-orange-50' },
                { label: '累计推广', value: `${MOCK_STATS.totalReferrals}人`, icon: 'ri-user-add-line', color: 'text-sky-500', bg: 'bg-sky-50' },
                { label: '活跃会员', value: `${MOCK_STATS.activeReferrals}人`, icon: 'ri-user-heart-line', color: 'text-rose-500', bg: 'bg-rose-50' },
                { label: '转化率', value: `${MOCK_STATS.conversionRate}%`, icon: 'ri-percent-line', color: 'text-violet-500', bg: 'bg-violet-50' },
              ].map((stat, i) => (
                <div key={i} className="bg-white rounded-xl p-4 border border-gray-100">
                  <div className={`w-9 h-9 flex items-center justify-center ${stat.bg} rounded-lg mb-3`}>
                    <i className={`${stat.icon} ${stat.color} text-lg`}></i>
                  </div>
                  <div className="text-xl font-bold text-gray-800">{stat.value}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Chart + Quick Actions */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {/* Bar Chart */}
              <div className="lg:col-span-2 bg-white rounded-xl p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-bold text-gray-800">月度收益趋势</h3>
                  <span className="text-xs text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full font-semibold">近6个月</span>
                </div>
                <div className="flex items-end gap-3 h-40">
                  {MONTHLY_DATA.map((d, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <div className="text-xs text-gray-500 font-medium">¥{d.amount}</div>
                      <div
                        className="w-full rounded-t-lg bg-gradient-to-t from-emerald-500 to-teal-400 transition-all duration-700"
                        style={{ height: `${(d.amount / maxAmount) * 120}px` }}
                      ></div>
                      <div className="text-xs text-gray-400">{d.month}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <h3 className="font-bold text-gray-800 mb-4">快捷操作</h3>
                <div className="flex flex-col gap-3">
                  {[
                    { icon: 'ri-links-line', label: '复制推广链接', color: 'text-emerald-600', bg: 'bg-emerald-50', action: () => { handleCopy(referralLinks.main); setActiveTab('links'); } },
                    { icon: 'ri-money-cny-circle-line', label: '查看结算记录', color: 'text-amber-600', bg: 'bg-amber-50', action: () => setActiveTab('payouts') },
                    { icon: 'ri-user-add-line', label: '查看推广记录', color: 'text-sky-600', bg: 'bg-sky-50', action: () => setActiveTab('referrals') },
                    { icon: 'ri-customer-service-line', label: '联系分销客服', color: 'text-rose-600', bg: 'bg-rose-50', action: () => {} },
                  ].map((item, i) => (
                    <button
                      key={i}
                      onClick={item.action}
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-all cursor-pointer text-left"
                    >
                      <div className={`w-9 h-9 flex items-center justify-center ${item.bg} rounded-lg flex-shrink-0`}>
                        <i className={`${item.icon} ${item.color}`}></i>
                      </div>
                      <span className="text-sm text-gray-700 font-medium">{item.label}</span>
                      <i className="ri-arrow-right-s-line text-gray-300 ml-auto"></i>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Commission Rules */}
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100 rounded-xl p-6">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <i className="ri-percent-line text-emerald-500"></i>
                佣金规则说明
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { plan: '工厂会员', rate: '5%', amount: '¥0.25/月', desc: '每成功推广1位工厂会员，获得月费5%佣金' },
                  { plan: '黄金会员', rate: '30%', amount: '¥3/月', desc: '每成功推广1位黄金会员，获得月费30%佣金' },
                  { plan: '年付黄金', rate: '30%', amount: '¥32.4/年', desc: '年付黄金会员佣金更高，推荐优先推广' },
                ].map((rule, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 border border-emerald-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-bold text-gray-800">{rule.plan}</span>
                      <span className="text-lg font-bold text-emerald-600">{rule.rate}</span>
                    </div>
                    <div className="text-base font-bold text-gray-700 mb-1">{rule.amount}</div>
                    <p className="text-xs text-gray-400 leading-relaxed">{rule.desc}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-4 flex items-center gap-1">
                <i className="ri-information-line text-emerald-400"></i>
                佣金每月15日统一结算，通过PayPal或银行转账发放。最低结算金额¥50。
              </p>
            </div>
          </div>
        )}

        {/* REFERRALS TAB */}
        {activeTab === 'referrals' && (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-50 flex items-center justify-between">
              <h3 className="font-bold text-gray-800">推广记录</h3>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">共 {MOCK_REFERRALS.length} 条记录</span>
                <span className="text-xs bg-emerald-50 text-emerald-600 px-3 py-1 rounded-full font-semibold">
                  待结算 ¥{MOCK_REFERRALS.filter(r => r.status === 'pending').reduce((s, r) => s + r.commission, 0)}
                </span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 text-xs text-gray-500 font-semibold">
                    <th className="text-left px-6 py-3">推广ID</th>
                    <th className="text-left px-6 py-3">用户</th>
                    <th className="text-left px-6 py-3">会员方案</th>
                    <th className="text-left px-6 py-3">注册日期</th>
                    <th className="text-right px-6 py-3">佣金</th>
                    <th className="text-center px-6 py-3">状态</th>
                  </tr>
                </thead>
                <tbody>
                  {MOCK_REFERRALS.map((r, i) => (
                    <tr key={r.id} className={`border-t border-gray-50 hover:bg-gray-50/50 transition-colors ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                      <td className="px-6 py-4 text-xs text-gray-400 font-mono">{r.id}</td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-700">{r.name}</td>
                      <td className="px-6 py-4">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${r.plan === '黄金会员' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-600'}`}>
                          {r.plan}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{r.date}</td>
                      <td className="px-6 py-4 text-right text-sm font-bold text-emerald-600">+¥{r.commission}</td>
                      <td className="px-6 py-4 text-center">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${r.status === 'paid' ? 'bg-green-50 text-green-600' : 'bg-orange-50 text-orange-600'}`}>
                          {r.status === 'paid' ? '已结算' : '待结算'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* PAYOUTS TAB */}
        {activeTab === 'payouts' && (
          <div>
            {/* Payout Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {[
                { label: '累计已结算', value: `¥${(MOCK_STATS.totalEarnings - MOCK_STATS.pendingPayout).toFixed(0)}`, icon: 'ri-check-double-line', color: 'text-green-500', bg: 'bg-green-50' },
                { label: '本期待结算', value: `¥${MOCK_STATS.pendingPayout.toFixed(0)}`, icon: 'ri-time-line', color: 'text-orange-500', bg: 'bg-orange-50' },
                { label: '下次结算日', value: '2026-05-15', icon: 'ri-calendar-check-line', color: 'text-sky-500', bg: 'bg-sky-50' },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-xl p-5 border border-gray-100 flex items-center gap-4">
                  <div className={`w-12 h-12 flex items-center justify-center ${item.bg} rounded-xl flex-shrink-0`}>
                    <i className={`${item.icon} ${item.color} text-xl`}></i>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-gray-800">{item.value}</div>
                    <div className="text-xs text-gray-400">{item.label}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Payout History */}
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-50">
                <h3 className="font-bold text-gray-800">结算历史</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 text-xs text-gray-500 font-semibold">
                      <th className="text-left px-6 py-3">结算期</th>
                      <th className="text-left px-6 py-3">结算日期</th>
                      <th className="text-left px-6 py-3">收款方式</th>
                      <th className="text-right px-6 py-3">金额</th>
                      <th className="text-center px-6 py-3">状态</th>
                    </tr>
                  </thead>
                  <tbody>
                    {MOCK_PAYOUTS.map((p, i) => (
                      <tr key={p.id} className={`border-t border-gray-50 hover:bg-gray-50/50 transition-colors ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                        <td className="px-6 py-4 text-sm font-medium text-gray-700">{p.period}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">{p.date}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1.5">
                            <i className={p.method === 'PayPal' ? 'ri-paypal-line text-sky-500' : 'ri-bank-line text-gray-400'}></i>
                            {p.method}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-bold text-emerald-600">¥{p.amount.toFixed(1)}</td>
                        <td className="px-6 py-4 text-center">
                          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                            p.status === 'paid' ? 'bg-green-50 text-green-600' : 'bg-orange-50 text-orange-600'
                          }`}>
                            {p.status === 'paid' ? '已到账' : '处理中'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="p-4 bg-gray-50 border-t border-gray-100">
                <p className="text-xs text-gray-400 flex items-center gap-1">
                  <i className="ri-information-line text-emerald-400"></i>
                  每月15日自动结算上月佣金，最低结算金额¥50。如有疑问请联系分销客服。
                </p>
              </div>
            </div>
          </div>
        )}

        {/* LINKS TAB */}
        {activeTab === 'links' && (
          <div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Link Generator */}
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <h3 className="font-bold text-gray-800 mb-5 flex items-center gap-2">
                  <i className="ri-links-line text-emerald-500"></i>
                  我的推广链接
                </h3>

                <div className="flex gap-2 mb-5 bg-gray-50 rounded-xl p-1">
                  {([
                    { id: 'main', label: '主页' },
                    { id: 'membership', label: '会员页' },
                    { id: 'shop', label: '商城' },
                  ] as { id: typeof linkType; label: string }[]).map(lt => (
                    <button
                      key={lt.id}
                      onClick={() => setLinkType(lt.id)}
                      className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${linkType === lt.id ? 'bg-white text-emerald-700' : 'text-gray-400 hover:text-gray-600'}`}
                    >
                      {lt.label}
                    </button>
                  ))}
                </div>

                <div className="bg-gray-50 rounded-xl p-4 mb-4">
                  <div className="text-xs text-gray-400 mb-2 font-medium">您的专属推广链接</div>
                  <div className="text-sm text-gray-700 font-mono break-all leading-relaxed">{referralLinks[linkType]}</div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleCopy(referralLinks[linkType])}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                    {copied ? '已复制！' : '复制链接'}
                  </button>
                  <button className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap">
                    <i className="ri-share-line"></i>
                  </button>
                </div>

                <div className="mt-4 p-3 bg-emerald-50 rounded-xl">
                  <p className="text-xs text-emerald-700 leading-relaxed">
                    <i className="ri-lightbulb-line mr-1"></i>
                    通过此链接注册的用户将自动绑定到您的推广账号，佣金实时计入。
                  </p>
                </div>
              </div>

              {/* Promo Materials */}
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <h3 className="font-bold text-gray-800 mb-5 flex items-center gap-2">
                  <i className="ri-image-line text-emerald-500"></i>
                  推广素材
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: '横幅广告 1200×628', icon: 'ri-image-2-line', size: '1200×628' },
                    { label: '方形图 1080×1080', icon: 'ri-image-line', size: '1080×1080' },
                    { label: '竖版故事 1080×1920', icon: 'ri-smartphone-line', size: '1080×1920' },
                    { label: '文字推广模板', icon: 'ri-file-text-line', size: 'TXT' },
                  ].map((mat, i) => (
                    <div key={i} className="border border-gray-100 rounded-xl p-4 hover:border-emerald-200 transition-all cursor-pointer group">
                      <div className="w-10 h-10 flex items-center justify-center bg-gray-50 group-hover:bg-emerald-50 rounded-lg mb-3 transition-all">
                        <i className={`${mat.icon} text-gray-400 group-hover:text-emerald-500 transition-all`}></i>
                      </div>
                      <div className="text-xs font-semibold text-gray-700 mb-1">{mat.label}</div>
                      <div className="text-xs text-gray-400">{mat.size}</div>
                      <button className="mt-2 text-xs text-emerald-600 font-medium flex items-center gap-1 cursor-pointer">
                        <i className="ri-download-line"></i>下载
                      </button>
                    </div>
                  ))}
                </div>

                <div className="mt-4 border-t border-gray-50 pt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">推广话术参考</h4>
                  <div className="bg-gray-50 rounded-xl p-4 text-xs text-gray-600 leading-relaxed">
                    "推荐大家试试富缘佳旺！这是一个真实认证的婚恋平台，每位会员都经过视频验证，安全可靠。工厂会员前3个月完全免费，黄金会员只要¥10/月，超值！点击我的链接注册：{referralLinks.main}"
                  </div>
                  <button
                    onClick={() => handleCopy(`推荐大家试试富缘佳旺！这是一个真实认证的婚恋平台，每位会员都经过视频验证，安全可靠。工厂会员前3个月完全免费，黄金会员只要¥10/月，超值！点击我的链接注册：${referralLinks.main}`)}
                    className="mt-2 text-xs text-emerald-600 font-medium flex items-center gap-1 cursor-pointer hover:text-emerald-700"
                  >
                    <i className="ri-file-copy-line"></i>复制话术
                  </button>
                </div>
              </div>
            </div>

            {/* Tips */}
            <div className="mt-6 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100 rounded-xl p-6">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <i className="ri-rocket-line text-emerald-500"></i>
                推广技巧
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { icon: 'ri-tiktok-line', title: '短视频平台', desc: '在抖音/TikTok发布婚恋话题内容，附上推广链接，转化率高' },
                  { icon: 'ri-wechat-line', title: '微信朋友圈', desc: '分享真实用户故事，配合推广链接，朋友圈信任度高' },
                  { icon: 'ri-instagram-line', title: 'Instagram', desc: '海外华人群体活跃，发布双语内容效果更佳' },
                  { icon: 'ri-youtube-line', title: 'YouTube', desc: '制作婚恋相关视频，在简介中放置推广链接' },
                ].map((tip, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 border border-emerald-100">
                    <div className="w-9 h-9 flex items-center justify-center bg-emerald-50 rounded-lg mb-3">
                      <i className={`${tip.icon} text-emerald-500`}></i>
                    </div>
                    <div className="text-sm font-semibold text-gray-800 mb-1">{tip.title}</div>
                    <p className="text-xs text-gray-400 leading-relaxed">{tip.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-8">
        <Footer />
      </div>
    </div>
  );
}
